export const hexToRgba = (hex, opacity) => {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r},${g},${b},${opacity})`;
};

/**
 * Network request utilities for handling request/response data in the steps preview
 */

const RESPONSE_TYPES = {
  FETCH_XHR: "Fetch/XHR",
  WEBSOCKET: "WS",
  JAVASCRIPT: "JS",
  CSS: "CSS",
  MEDIA: "Media",
  FONT: "Font",
  DOCUMENT: "Doc",
  OTHER: "Other",
};

const DEFAULT_HEADERS = {
  request: {
    "Content-Type": "application/json",
    "User-Agent": "Mozilla/5.0",
    Accept: "*/*",
  },
  response: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Server: "TestServer",
  },
};

/**
 * Determines response type based on explicit type or URL pattern
 * @param {Object} data - Request data
 * @returns {string} The determined response type
 */
function determineResponseType(data) {
  if (data.type) {
    switch (data.type) {
      case "fetch":
      case "xhr":
        return RESPONSE_TYPES.FETCH_XHR;
      case "websocket":
        return RESPONSE_TYPES.WEBSOCKET;
      case "js":
        return RESPONSE_TYPES.JAVASCRIPT;
      case "css":
        return RESPONSE_TYPES.CSS;
      case "media":
        return RESPONSE_TYPES.MEDIA;
      case "font":
        return RESPONSE_TYPES.FONT;
      case "doc":
        return RESPONSE_TYPES.DOCUMENT;
      // If type is unknown, fall through to URL-based detection
    }
  }

  // URL-based detection
  const url = data.fullUrl;
  if (url.includes(".css")) return RESPONSE_TYPES.CSS;
  if (url.includes(".js")) return RESPONSE_TYPES.JAVASCRIPT;
  if (url.includes(".png") || url.includes(".jpg") || url.includes(".svg"))
    return RESPONSE_TYPES.MEDIA;
  if (url.includes(".woff") || url.includes(".ttf")) return RESPONSE_TYPES.FONT;
  if (url.includes(".html") || url.includes(".htm"))
    return RESPONSE_TYPES.DOCUMENT;
  if (url.includes("/api/") || data.method !== "GET")
    return RESPONSE_TYPES.FETCH_XHR;

  return RESPONSE_TYPES.OTHER;
}

/**
 * Formats size in bytes to a human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size (e.g., "1.2 KiB" or "100 B")
 */
function formatSize(bytes) {
  if (bytes > 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${bytes} B`;
}

/**
 * Generates response time, either using provided duration or a plausible random value
 * @param {Object} data - Request data that may contain duration
 * @returns {string} Formatted time string (e.g., "150 ms")
 */
function generateResponseTime(data) {
  if (data.duration) {
    return `${data.duration} ms`;
  }
  // Generate plausible random time between 10-1010 ms
  return `${Math.floor(Math.random() * 1000) + 10} ms`;
}

/**
 * Maps response type to content type
 * @param {string} responseType - The high-level response type
 * @returns {string} The appropriate content type
 */
function getContentType(responseType) {
  switch (responseType) {
    case RESPONSE_TYPES.MEDIA:
      return "image";
    case RESPONSE_TYPES.JAVASCRIPT:
      return "javascript";
    case RESPONSE_TYPES.CSS:
      return "css";
    case RESPONSE_TYPES.WEBSOCKET:
      return "websocket";
    default:
      return "json";
  }
}

/**
 * Creates a complete network request object from the provided data
 * @param {Object} data - Base request data
 * @returns {Object} Complete network request object
 */
const createNetworkRequestObj = (data) => {
  // Determine response type
  const responseType = determineResponseType(data);

  // Calculate size
  const size =
    typeof data.responseContent === "string" ? data.responseContent.length : 0;
  const sizeFormatted = formatSize(size);

  // Generate response time
  const time = generateResponseTime(data);

  // Process headers
  const headers = {
    request: {
      ...DEFAULT_HEADERS.request,
      ...((data.headers && data.headers.request) || {}),
    },
    response: {
      ...DEFAULT_HEADERS.response,
      ...((data.headers && data.headers.response) || {}),
    },
  };

  return {
    id: data.id,
    name: data.name,
    fullUrl: data.fullUrl,
    method: data.method,
    status: data.status,
    statusText:
      data.statusText ||
      (data.status >= 200 && data.status < 300 ? "OK" : "Error"),
    domain: data.domain,
    type: responseType,
    frame: 0,
    size: sizeFormatted,
    time,
    waterfall: "",
    responseContent: data.responseContent,
    timestamp: data.timestamp,
    headers,
    requestPayload: data.requestPayload,
    responseType: getContentType(responseType),
    rawType: data.type, // Store the original raw type
    error: data.error || false,
  };
};

// Export the main function
export default createNetworkRequestObj;

export const getStatusColor = (status: number) => {
  if (status >= 200 && status < 300) return "#4caf50"; // Green for success
  if (status >= 300 && status < 400) return "#ff9800"; // Orange for redirect
  if (status >= 400) return "#f44336"; // Red for error
  return "#757575"; // Gray for unknown
};

export const getTypeIcon = (type: string) => {
  switch (type) {
    case "Fetch/XHR":
      return "🌐";
    case "WS":
      return "⚡";
    case "JS":
      return "⚙️";
    case "CSS":
      return "🎨";
    case "Media":
      return "🖼️";
    case "Font":
      return "🔤";
    case "Doc":
      return "📄";
    default:
      return "📦";
  }
};
export function detectOS(userAgent) {
  let os = "Unknown";
  let osVersion = "Unknown";

  if (userAgent.includes("Windows NT 10.0")) {
    os = "Windows";

    // Check for Windows 11 by looking at build number or other indicators
    // Windows 11 typically has build numbers 22000 or higher
    const buildMatch = userAgent.match(/build\/(\d+)/i);
    const buildNumber = buildMatch ? parseInt(buildMatch[1]) : 0;

    // You can also check for feature indicators present in Windows 11
    const isWin11 = buildNumber >= 22000 || userAgent.includes("Windows 11");

    osVersion = isWin11 ? "11" : "10";
  } else if (userAgent.includes("Windows NT 6.3"))
    (os = "Windows"), (osVersion = "8.1");
  else if (userAgent.includes("Windows NT 6.2"))
    (os = "Windows"), (osVersion = "8");
  else if (userAgent.includes("Mac OS X")) {
    os = "MacOS";
    osVersion =
      userAgent.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, ".") ||
      "Unknown";
  } else if (userAgent.includes("Linux")) {
    os = "Linux";
  } else if (userAgent.includes("Android")) {
    os = "Android";
  } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
    os = "iOS";
  }

  return { os, osVersion };
}
export function getCSSPath(el) {
  if (!el) return null;

  if (el.classList.length === 0) return null;
  let selector =
    el.nodeName.toLowerCase() + "." + Array.from(el.classList).join(".");

  return selector;
}
export const determineBestSelector = (targetId, xPaths, cssPath) => {
  if (targetId) return "id";
  if (xPaths?.idBasedXPath) return "idBasedXPath";
  if (xPaths?.classBasedXPath || xPaths?.classBasedPositionXPath)
    return "classBasedXPath";
  if (xPaths?.parentBasedXPath || xPaths?.parentBasedIndexXPath)
    return "parentBasedXPath";
  if (xPaths?.textBasedXPath) return "textBasedXPath";
  if (xPaths?.simpleTagXPath) return "simpleTagXPath";
  if (cssPath) return "cssPath";
  return "element";
};

interface XPathCollection {
  prioritizedXPath: string;
  idBasedXPath?: string;
  textBasedXPath?: string;
  classBasedXPath?: string;
  classBasedPositionXPath?: string;
  parentBasedXPath?: string;
  parentBasedIndexXPath?: string;
  simpleTagXPath?: string;
  roleXPath?: string;
  roleWithAriaLabelXPath?: string;
  roleWithNameXPath?: string;
  roleWithPositionXPath?: string;
  contentEditableXPath?: string;
  contentEditablePositionXPath?: string;
  contentEditableWithParentIdXPath?: string;
}

/**
 * Generates all possible XPaths for an element and prioritizes them
 */
export const generateAllXPaths = (element: HTMLElement): XPathCollection => {
  if (!element) return { prioritizedXPath: "" };

  const xpaths: XPathCollection = { prioritizedXPath: "" };

  // Generate all types of XPaths
  generateIdBasedXPath(element, xpaths);
  generateContentEditableXPath(element, xpaths);
  generateTextBasedXPath(element, xpaths);
  generateClassBasedXPaths(element, xpaths);
  generateParentBasedXPaths(element, xpaths);
  generateSimpleTagXPath(element, xpaths);
  generateRoleBasedXPaths(element, xpaths);

  // Determine the prioritized XPath
  xpaths.prioritizedXPath = determinePrioritizedXPath(xpaths);

  return xpaths;
};

/**
 * Generates an XPath based on the element's ID
 */
function generateIdBasedXPath(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  if (element.id) {
    xpaths.idBasedXPath = `//*[@id='${element.id}']`;
  }
}

/**
 * Generates XPath for contenteditable elements
 */
function generateContentEditableXPath(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  if (!element.hasAttribute("contenteditable")) return;

  // Try to use the parent element as context if it has an ID
  const parent = element.parentElement;
  if (parent && parent.id) {
    xpaths.contentEditableWithParentIdXPath = `//*[@id='${parent.id}']//*[@contenteditable='true']`;
  }

  const tagName = element.tagName.toLowerCase();
  // Find all contenteditable elements of the same tag
  const allContentEditables = document.querySelectorAll(
    `${tagName}[contenteditable='true']`
  );

  // If there's only one, we can use a simple selector
  if (allContentEditables.length === 1) {
    xpaths.contentEditableXPath = `//${tagName}[@contenteditable='true']`;
  } else {
    // If there are multiple, determine the position
    const position = Array.from(allContentEditables).indexOf(element) + 1;
    xpaths.contentEditablePositionXPath = `(//${tagName}[@contenteditable='true'])[${position}]`;
  }
}
/**
 * Generates an XPath based on the element's text content
 */
function generateTextBasedXPath(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  const isTextEligible =
    element.textContent?.trim() &&
    !["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) &&
    !element.hasAttribute("contenteditable") &&
    element.textContent.trim().length < 50;

  if (isTextEligible) {
    const text = element.textContent?.trim() || "";
    const escapedText = text.replace(/'/g, "\\'");
    xpaths.textBasedXPath = `//${element.tagName.toLowerCase()}[contains(.,'${escapedText}')]`;
  }
}

/**
 * Generates XPaths based on the element's class
 */
function generateClassBasedXPaths(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  if (
    !element.className ||
    typeof element.className !== "string" ||
    !element.className.trim()
  ) {
    return;
  }

  const classNames = element.className.trim().split(/\s+/);
  const mainClass = classNames[0];
  const tagName = element.tagName.toLowerCase();

  // Special handling for div elements
  if (tagName === "div") {
    handleDivClassXPaths(element, classNames, xpaths);
  } else {
    handleStandardClassXPaths(element, mainClass, xpaths);
  }
}

/**
 * Handles class-based XPaths for div elements
 */
function handleDivClassXPaths(
  element: HTMLElement,
  classNames: string[],
  xpaths: XPathCollection
): void {
  let classSelector = "";

  if (classNames.length >= 2) {
    classSelector = `contains(@class, '${classNames[0]}') and contains(@class, '${classNames[1]}')`;
  } else {
    classSelector = `contains(@class, '${classNames[0]}')`;
  }

  xpaths.classBasedXPath = `//div[${classSelector}]`;

  // Add position if needed
  const matchingElements = evaluateXPath(`//div[${classSelector}]`);
  if (matchingElements.snapshotLength > 1) {
    const position = findElementPosition(element, matchingElements);
    if (position > 0) {
      xpaths.classBasedPositionXPath = `(//div[${classSelector}])[${position}]`;
    }
  }
}

/**
 * Handles class-based XPaths for non-div elements
 */
function handleStandardClassXPaths(
  element: HTMLElement,
  mainClass: string,
  xpaths: XPathCollection
): void {
  const tagName = element.tagName.toLowerCase();

  xpaths.classBasedXPath = `//*[contains(@class, '${mainClass}')]`;

  // Add position if needed
  const xpath = `//${tagName}[contains(@class, '${mainClass}')]`;
  const matchingElements = evaluateXPath(xpath);

  if (matchingElements.snapshotLength > 1) {
    const position = findElementPosition(element, matchingElements);
    if (position > 0) {
      xpaths.classBasedPositionXPath = `(${xpath})[${position}]`;
    }
  }
}

/**
 * Generates XPaths based on the element's parent
 */
function generateParentBasedXPaths(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  const parent = element.parentElement;
  if (!parent) return;

  // Using the original generatePrioritizedXPath for parent to avoid circular reference
  const parentXPath = generatePrioritizedXPath(parent);
  const tagName = element.tagName.toLowerCase();

  const childElements = evaluateXPath(`${parentXPath}/${tagName}`);

  if (childElements.snapshotLength === 1) {
    xpaths.parentBasedXPath = `${parentXPath}/${tagName}`;
  } else {
    const position = findElementPosition(element, childElements);
    if (position > 0) {
      xpaths.parentBasedIndexXPath = `${parentXPath}/${tagName}[${position}]`;
    }
  }
}

/**
 * Generates a simple tag-based XPath with position
 */
function generateSimpleTagXPath(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  const tagName = element.tagName.toLowerCase();
  const allElementsWithTag = evaluateXPath(`//${tagName}`);

  const position = findElementPosition(element, allElementsWithTag);
  if (position > 0) {
    xpaths.simpleTagXPath = `//${tagName}[${position}]`;
  }
}

/**
 * Generates XPaths based on ARIA role attributes
 */
function generateRoleBasedXPaths(
  element: HTMLElement,
  xpaths: XPathCollection
): void {
  const role = element.getAttribute("role");
  if (!role) return;

  // Role with aria-label
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) {
    xpaths.roleWithAriaLabelXPath = `//*[@role='${role}' and @aria-label='${ariaLabel}']`;
  }

  // Role with name
  const name = element.getAttribute("name");
  if (name) {
    xpaths.roleWithNameXPath = `//*[@role='${role}' and @name='${name}']`;
  }

  // Role with position
  const sameRoleElements = document.querySelectorAll(`[role='${role}']`);
  if (sameRoleElements.length > 1) {
    const position = Array.from(sameRoleElements).indexOf(element) + 1;
    xpaths.roleWithPositionXPath = `(//*[@role='${role}'])[${position}]`;
  } else {
    xpaths.roleXPath = `//*[@role='${role}']`;
  }
}

/**
 * Helper function to evaluate an XPath expression
 */
function evaluateXPath(xpath: string): XPathResult {
  return document.evaluate(
    xpath,
    document,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null
  );
}

/**
 * Helper function to find the position of an element in an XPath result
 */
function findElementPosition(
  element: HTMLElement,
  snapshotResult: XPathResult
): number {
  for (let i = 0; i < snapshotResult.snapshotLength; i++) {
    if (snapshotResult.snapshotItem(i) === element) {
      return i + 1;
    }
  }
  return 0;
}

/**
 * Determines the prioritized XPath based on the priority order
 */
function determinePrioritizedXPath(xpaths: XPathCollection): string {
  // Follow priority order
  if (xpaths.idBasedXPath) return xpaths.idBasedXPath;
  if (xpaths.contentEditableWithParentIdXPath)
    return xpaths.contentEditableWithParentIdXPath;
  if (xpaths.contentEditableXPath) return xpaths.contentEditableXPath;
  if (xpaths.contentEditablePositionXPath)
    return xpaths.contentEditablePositionXPath;
  if (xpaths.textBasedXPath) return xpaths.textBasedXPath;
  if (xpaths.classBasedPositionXPath) return xpaths.classBasedPositionXPath;
  if (xpaths.classBasedXPath) return xpaths.classBasedXPath;
  if (xpaths.parentBasedIndexXPath) return xpaths.parentBasedIndexXPath;
  if (xpaths.parentBasedXPath) return xpaths.parentBasedXPath;
  if (xpaths.simpleTagXPath) return xpaths.simpleTagXPath;

  return "";
}

// Note: This function is referenced in the refactored code but not included
// You'll need to provide this implementation or adjust the code accordingly
function generatePrioritizedXPath(element: HTMLElement): string {
  if (!element) return "";

  // Simple implementation that follows the same priority order
  if (element.id) {
    return `//*[@id='${element.id}']`;
  }

  const tagName = element.tagName.toLowerCase();
  const allElementsWithTag = document.evaluate(
    `//${tagName}`,
    document,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null
  );

  let position = 0;
  for (let i = 0; i < allElementsWithTag.snapshotLength; i++) {
    if (allElementsWithTag.snapshotItem(i) === element) {
      position = i + 1;
      break;
    }
  }

  return `//${tagName}[${position}]`;
}

export const generateRandomLightColor = (): string => {
  // Generate higher values for RGB to ensure light colors (180-240 range)
  const r = Math.floor(Math.random() * 60) + 180;
  const g = Math.floor(Math.random() * 60) + 180;
  const b = Math.floor(Math.random() * 60) + 180;
  return `rgba(${r}, ${g}, ${b}, 0.3)`;
};

export const generateUniqueColor = (existingColors: string[]): string => {
  let newColor: string;
  let isUnique = false;

  while (!isUnique) {
    newColor = generateRandomLightColor();
    // Check if the color is unique enough from existing colors
    isUnique = !existingColors.some((color) => {
      // Simple string comparison is enough for our purposes
      return color === newColor;
    });
  }

  return newColor;
};
